import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { Loading } from 'components/uikit/suspense/Loading';
import { QueryFactory } from 'services/api';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Input } from 'components/uikit/inputs/Input';

import styles from './DeleteProjectModal.module.scss';
import { Field } from 'components/uikit/Field';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast';

type DeleteProjectModalProps = {
  projectId: number;
  projectName: string;
  onClose: () => void;
  isOpen: boolean;
  afterDelete?: () => void;
};

export const DeleteProjectModal: React.FC<DeleteProjectModalProps> = (
  props,
) => {
  const i18n = useScopedTranslation('Component.DeleteProjectModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState(String);

  const isDeleteDisabled = projectName !== props.projectName;

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isBlocking={true}
      title={i18n.t('title')}
    >
      <Loading loading={loading}>
        <div className={styles.message}>
          <p>
            <Trans
              i18nKey="Component.DeleteProjectModal.message_line_1"
              values={{ projectName: props.projectName }}
              components={{
                red: <span className={styles.danger} />,
                pre: <span className={styles.noWhiteSpace} />,
              }}
            />
          </p>
          <p className={styles.subMessage}>{i18n.t('message_line_2')}</p>
          <Field
            className={styles.confirmField}
            title={i18n.t('confirm_deleting_field')}
          >
            <Input
              value={projectName}
              onChange={(e) => {
                const value = e.target.value;
                setProjectName(value);
              }}
            ></Input>
          </Field>
        </div>
        <div className={styles.footer}>
          <Button
            title={buttonTitle.t('cancel')}
            color={ButtonColor.Secondary}
            className={styles.button}
            onClick={async () => {
              props.onClose();
            }}
          />
          <Button
            type={'submit'}
            title={buttonTitle.t('delete')}
            color={ButtonColor.DestroyPrimary}
            className={styles.button}
            disabled={isDeleteDisabled}
            onClick={async () => {
              try {
                setLoading(true);
                await QueryFactory.ProjectClient.delete_(props.projectId);
                await queryClient.invalidateQueries({
                  queryKey: QueryFactory.ProjectQuery.searchQueryKey(),
                });
                props.onClose();
                props.afterDelete?.();
              } catch (e) {
                showErrorToast(e);
              } finally {
                setLoading(false);
              }
            }}
          />
        </div>
      </Loading>
    </CustomModal>
  );
};
